import api from '@/config/api'
import type { ApiResponse } from '@/shared/types/api'
import type { AgendaCompletDto, TacheAgenda } from '@/shared/types/agenda'

function formatDate(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export const agendaApi = {
  /** Get full agenda for a student on a given date */
  getAgendaComplet: async (etudiantId: number, date: Date): Promise<AgendaCompletDto> => {
    const res = await api.get<ApiResponse<AgendaCompletDto>>(
      `/api/agenda-complet/etudiant/${etudiantId}/date/${formatDate(date)}`,
    )
    return res.data.data
  },

  /** Update timer (seconds remaining) on a study task */
  updateTacheTimer: async (tacheId: number, timerSeconds: number): Promise<void> => {
    await api.put(`/api/taches-agenda/${tacheId}/timer`, timerSeconds, {
      headers: { 'Content-Type': 'application/json' },
    })
  },

  /** Mark a study task as done */
  terminerTache: async (tacheId: number): Promise<void> => {
    await api.put(`/api/taches-agenda/${tacheId}/terminer`, true, {
      headers: { 'Content-Type': 'application/json' },
    })
  },

  /** Cancel completion of a study task and restore timer */
  annulerTache: async (tacheId: number, timerSeconds?: number): Promise<void> => {
    await api.put(`/api/taches-agenda/${tacheId}/terminer`, false, {
      headers: { 'Content-Type': 'application/json' },
    })
    if (timerSeconds != null && timerSeconds > 0) {
      await api.put(`/api/taches-agenda/${tacheId}/timer`, timerSeconds, {
        headers: { 'Content-Type': 'application/json' },
      })
    }
  },

  /** Mark a language task as done */
  terminerTacheLangue: async (tacheLangueId: number): Promise<void> => {
    await api.put(`/api/taches-agenda-langue/${tacheLangueId}/terminer`, true, {
      headers: { 'Content-Type': 'application/json' },
    })
  },

  /** Cancel completion of a language task and restore timer */
  annulerTacheLangue: async (tacheLangueId: number, timerSeconds?: number): Promise<void> => {
    await api.put(`/api/taches-agenda-langue/${tacheLangueId}/terminer`, false, {
      headers: { 'Content-Type': 'application/json' },
    })
    if (timerSeconds != null && timerSeconds > 0) {
      await api.put(`/api/taches-agenda-langue/${tacheLangueId}/timer`, timerSeconds, {
        headers: { 'Content-Type': 'application/json' },
      })
    }
  },

  /** Report a task to the next day */
  reporterTache: async (tacheId: number): Promise<TacheAgenda | null> => {
    const res = await api.put<ApiResponse<TacheAgenda | null>>(
      `/api/taches-agenda/${tacheId}/reporter`,
    )
    return res.data.data
  },

  /** Recover a reported task back to today */
  recupererTache: async (tacheId: number): Promise<TacheAgenda> => {
    const res = await api.put<ApiResponse<TacheAgenda>>(
      `/api/taches-agenda/${tacheId}/recuperer`,
    )
    return res.data.data
  },

  /** Update timer on an activity */
  updateActivityTimer: async (activityId: number, timerSeconds: number): Promise<void> => {
    await api.put(`/api/activities/${activityId}/timer`, timerSeconds, {
      headers: { 'Content-Type': 'application/json' },
    })
  },

  /** Mark an activity as done for a specific date */
  terminerActivity: async (activityId: number, date: Date): Promise<void> => {
    await api.put(`/api/activities/${activityId}/terminer?date=${formatDate(date)}`, true, {
      headers: { 'Content-Type': 'application/json' },
    })
  },

  /** Cancel completion of an activity for a specific date */
  annulerActivity: async (activityId: number, date: Date): Promise<void> => {
    await api.put(`/api/activities/${activityId}/terminer?date=${formatDate(date)}`, false, {
      headers: { 'Content-Type': 'application/json' },
    })
  },
}
